import { useEffect, useLayoutEffect } from "react";

import { useTenantBranding } from "@/api/queries/branding";
import { useShellNav } from "@/shell/useShellNav";

/**
 * Lightweight SEO helper — writes `<title>`, `<meta name="description">`,
 * Open Graph tags, `<link rel="canonical">`, `<meta name="robots">` and
 * `<link rel="alternate" hreflang>` alternates for every supported
 * language, directly into `document.head`.
 *
 * We deliberately avoid react-helmet-async: the site is a static build
 * with no SSR, and the runtime cost of a helmet provider would
 * outweigh the benefit of managing a few tags imperatively. On every
 * route change the hook re-applies its tags and leaves the rest of
 * `<head>` untouched.
 *
 * Relationship to the edge middleware
 * -----------------------------------
 * `apps/web/middleware.ts` writes a subset of these same tags into the
 * HTML *before* it reaches the browser, for the crawlers that never
 * run this code (Facebook, LinkedIn, Slack, X read raw markup). The
 * two are not redundant: the injected values describe the URL the
 * document was **served** from and go stale on the first client-side
 * navigation, which is exactly what this hook fixes. `upsertMeta` /
 * `upsertCanonical` match on the same selectors the middleware emits,
 * so they update those tags in place rather than duplicating them.
 *
 * Title format
 * ------------
 * Callers pass the **page name only** (`"Ceník"`, `"Cart"`); the hook
 * composes `${pageName} · ${brandName}`, where `brandName` is the
 * tenant's ``OrgBranding.name`` on the tenant shell and ``"Forgmatic"``
 * everywhere else (platform marketing / platform admin). A tenant
 * shell whose branding is still loading (or has no name) gets the page
 * name alone — never the platform's (KAN-1681). Legacy call sites
 * that hand-rolled the suffix (e.g. ``"Ceník · Forgmatic"``) still
 * work — ``stripLegacyBrandSuffix`` peels the trailing ``· Forgmatic``
 * / ``— Forgmatic`` / ``- Forgmatic`` before composition so the
 * brand isn't duplicated.
 */

import { stripEditMarkers } from "@/components/edit/editModeStore";
import {
  getActiveLangCodes,
  getRuntimeDefaultLang,
} from "@/i18n/languageRegistry";

const DEFAULT_BRAND_NAME = "Forgmatic";

/**
 * Strip a trailing ``· <Brand>`` / ``— <Brand>`` / ``- <Brand>``
 * suffix from a legacy title so the brand isn't double-appended.
 *
 * Matches the literal default brand (``"Forgmatic"``) as well as the
 * resolved tenant brand — callers that already substituted the tenant
 * name (or used ``app.name`` from i18n) end up with whatever the
 * runtime resolved, so we strip both.
 */
function stripLegacyBrandSuffix(title: string, tenantName?: string): string {
  const brands = [DEFAULT_BRAND_NAME];
  if (tenantName && tenantName !== DEFAULT_BRAND_NAME) {
    brands.push(tenantName);
  }
  for (const brand of brands) {
    // Escape regex metacharacters in the brand name (tenant brands
    // can contain dots, parens, etc.).
    const escaped = brand.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`\\s*[·\\-—]\\s*${escaped}\\s*$`, "u");
    const stripped = title.replace(re, "");
    if (stripped !== title) {
      return stripped;
    }
  }
  return title;
}

interface SeoParams {
  /**
   * Page name without brand suffix. The hook composes
   * ``"<page> · <brand>"``. Legacy values that already include a
   * brand suffix are detected and stripped (see
   * ``stripLegacyBrandSuffix``).
   *
   * Pass an empty string when there's no meaningful page name (e.g.
   * the brand homepage) — the hook will use just the brand.
   */
  title: string;
  description?: string;
  /** Path relative to the origin, starting with ``/``. Used for hreflang alternates. */
  path: string;
  /**
   * Language-stripped path per language code, for pages whose address
   * differs by language (a changelog article, KAN-1971). A language
   * missing here falls back to ``path``.
   */
  alternatePaths?: Record<string, string>;
  lang: string;
  image?: string;
  /**
   * ``<meta name="robots">`` content. Omit on indexable pages — the
   * hook then *removes* any tag a previous route left behind, which
   * is the whole reason this is a managed tag rather than something
   * a page renders itself: an SPA navigation off a ``noindex`` route
   * would otherwise carry the directive onto the next page.
   *
   * Set ``"noindex, follow"`` on routes that answer HTTP 200 but
   * aren't a page — the SPA 404 above all. The static host serves
   * ``index.html`` for every unmatched path, so a typo'd URL is a
   * soft 404. The platform host also gets a real 404 from the edge
   * (KAN-1955, ``PLATFORM_ROUTE_PATHS`` in ``middleware.ts``); tenant
   * hosts have per-tenant routes and rely on this tag alone.
   */
  robots?: string;
  /**
   * Call site role for effect ordering. Layout-level callers
   * (``AdminLayout``, ``ShellFrame``) pass ``"layout"`` so their
   * write commits via ``useLayoutEffect`` *before* a page-level
   * ``useSeo`` (default ``"page"``) writes via ``useEffect``. Without
   * this, React's child-before-parent effect order would make the
   * layout's default title override the page's specific title.
   * Defaults to ``"page"``.
   */
  priority?: "layout" | "page";
}

function upsertMeta(
  selector: string,
  attrName: "name" | "property",
  attrValue: string,
  content: string,
): void {
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attrName, attrValue);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, hreflang: string, href: string): void {
  const selector = `link[rel="${rel}"][hreflang="${hreflang}"]`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>(
    'link[rel="canonical"]',
  );
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/**
 * Write ``<meta name="robots">``, or remove it when ``content`` is
 * undefined.
 *
 * The removal branch is the point. The edge middleware and the SPA
 * 404 both write this tag; without a managed removal, navigating
 * from a ``noindex`` route to an indexable one would leave the
 * directive in ``<head>`` and quietly de-index the destination.
 */
function setRobots(content: string | undefined): void {
  const el = document.head.querySelector<HTMLMetaElement>(
    'meta[name="robots"]',
  );
  if (!content) {
    el?.remove();
    return;
  }
  if (el) {
    el.setAttribute("content", content);
    return;
  }
  const created = document.createElement("meta");
  created.setAttribute("name", "robots");
  created.setAttribute("content", content);
  document.head.appendChild(created);
}

export function useSeo({
  title,
  description,
  path,
  alternatePaths,
  lang,
  image,
  robots,
  priority = "page",
}: SeoParams): void {
  const shellNav = useShellNav();
  const isTenant = shellNav.shell === "tenant";
  const branding = useTenantBranding({ enabled: isTenant });
  const tenantName = branding.data?.name?.trim();
  const tenantDescription = branding.data?.meta_description?.trim();
  const tenantImage = branding.data?.og_image_url?.trim();

  const apply = (): void => {
    // KAN-1681 — obchod tenanta nikdy nepodepisuje platforma: dokud se
    // branding nenačte (nebo když jméno firmy chybí), je titulek jen
    // název stránky. Údaje tenanta nepadají na platformní.
    const brandName = isTenant ? tenantName : DEFAULT_BRAND_NAME;
    // Značky inline editoru (zero-width) do titulku nepatří - a bránily by
    // oříznutí „· Forgmatic“, takže titulek měl značku dvakrát (KAN-1816).
    const pageName = stripLegacyBrandSuffix(
      stripEditMarkers(title).trim(),
      tenantName,
    ).trim();
    const effectiveTitle =
      pageName && brandName
        ? `${pageName} · ${brandName}`
        : pageName || brandName || "";
    const effectiveDescription =
      (description ? stripEditMarkers(description) : description) ||
      tenantDescription;
    // KAN-1955 — the platform's own pages share with a card image
    // (``public/og``, 1200×630). A tenant shop never gets it: without
    // its own image the shop shares as text, not as a Forgmatic ad.
    const platformImage = isTenant
      ? undefined
      : `/og/forgmatic-${lang === "cs" ? "cs" : "en"}.png`;
    const effectiveImage = image || tenantImage || platformImage;

    document.title = effectiveTitle;
    document.documentElement.lang = lang;

    if (effectiveDescription) {
      upsertMeta(
        'meta[name="description"]',
        "name",
        "description",
        effectiveDescription,
      );
    }

    const origin =
      typeof window !== "undefined" ? window.location.origin : "";

    // Absolute URL of ``path`` in language ``l``. ``path`` is
    // language-stripped — the caller passes a canonical path like
    // ``/cart`` — so this is the single place the language prefix is
    // applied, shared by the canonical and every hreflang alternate.
    // The trailing slash is dropped so the brand homepage
    // (``path === "/"``) resolves to ``/cs`` rather than ``/cs/``,
    // matching both the router's own URL and what the edge
    // middleware injects.
    const pathInLang = (l: string) =>
      `${origin}/${l}${alternatePaths?.[l] ?? path}`.replace(/\/+$/, "");

    upsertMeta(
      'meta[property="og:title"]',
      "property",
      "og:title",
      effectiveTitle,
    );
    if (effectiveDescription) {
      upsertMeta(
        'meta[property="og:description"]',
        "property",
        "og:description",
        effectiveDescription,
      );
    }
    // Language-prefixed, like the canonical and the alternates.
    // ``path`` alone yields ``/pricing-plans``, which is not a route
    // the router serves — everything mounts under ``/:lang`` — so the
    // shared link previews pointed at a URL that redirects.
    upsertMeta('meta[property="og:url"]', "property", "og:url", pathInLang(lang));
    upsertMeta('meta[property="og:type"]', "property", "og:type", "website");
    upsertMeta(
      'meta[property="og:locale"]',
      "property",
      "og:locale",
      lang === "cs" ? "cs_CZ" : "en_US",
    );
    if (effectiveImage) {
      upsertMeta(
        'meta[property="og:image"]',
        "property",
        "og:image",
        effectiveImage.startsWith("http")
          ? effectiveImage
          : `${origin}${effectiveImage}`,
      );
    }
    upsertMeta(
      'meta[name="twitter:card"]',
      "name",
      "twitter:card",
      "summary_large_image",
    );

    // Canonical. The edge middleware injects one server-side for the
    // crawlers that don't run JS; re-applying it here is what keeps
    // it correct after a client-side route change — the injected
    // value names the URL the document was *served* from, which stops
    // being the URL the user is on the moment they click a link.
    //
    // Built from `lang` + `path` rather than `location`: that's the
    // same pair the hreflang alternates below use, so the canonical
    // and the alternate for the current language are guaranteed to be
    // the same string. Reading `location` instead would fold in the
    // query (`?utm_source=…`, `?help=…` — same document) and drift
    // from the alternates on any path the caller normalised.
    upsertCanonical(pathInLang(lang));
    setRobots(robots);

    // hreflang alternates for every supported language + x-default.
    for (const l of getActiveLangCodes()) {
      upsertLink("alternate", l, pathInLang(l));
    }
    upsertLink("alternate", "x-default", pathInLang(getRuntimeDefaultLang()));
  };

  // Both hooks are called unconditionally on every render (Rules of
  // Hooks) — ``priority`` only decides which one actually writes.
  // useLayoutEffect commits synchronously pre-paint (parents AFTER
  // children, but BEFORE any useEffect anywhere in the tree). So a
  // page's default-priority ``useEffect`` always overrides a layout's
  // ``"layout"``-priority ``useLayoutEffect``, regardless of tree
  // depth — which is the exact semantic we want for shell defaults +
  // per-page overrides.
  // A fresh object every render; the effects key on its content.
  const alternatesKey = JSON.stringify(alternatePaths ?? null);
  useLayoutEffect(() => {
    if (priority === "layout") apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    description,
    path,
    alternatesKey,
    lang,
    image,
    robots,
    isTenant,
    tenantName,
    tenantDescription,
    tenantImage,
    priority,
  ]);
  useEffect(() => {
    if (priority === "page") apply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    title,
    description,
    path,
    alternatesKey,
    lang,
    image,
    robots,
    isTenant,
    tenantName,
    tenantDescription,
    tenantImage,
    priority,
  ]);
}
